import React from 'react';
import { CheckSquare, MinusSquare, XSquare } from '@phosphor-icons/react';
import {
  StatusBadge,
  normalizeRunStatus,
  MultiSegmentBar,
  TagBadge,
  Text,
  useTheme,
  statusHexColors,
  type GaugeSegment,
} from '@chatsdet/ui';
import { parseTagFilter } from '@chatsdet/types';
import type {
  RunsListRow,
  ExternalRunSummaryDto,
  WorkflowTestExecutionDto,
} from '../../features/workflows/useWorkflowTestExecutions';
import { useTagsQuery, type Tag } from '../../hooks/useTags';

type Props = {
  execution: RunsListRow;
};

/**
 * Status cell for an external-suite run. Reuses the same StatusBadge /
 * MultiSegmentBar as native rows (blue=pass, orange=fail — red is destructive
 * only) so external rows sit visually alongside native ones in the standard
 * table, tagged "External".
 */
const ExternalStatusCell: React.FC<{ run: ExternalRunSummaryDto }> = ({ run }) => {
  const { config } = useTheme();
  const colors = statusHexColors[config.theme];
  const externalTag = (
    <TagBadge name="External" color="gray" size="sm" className="self-start" hideDot />
  );

  if (run.status !== 'sealed') {
    const labelMap: Record<string, string> = {
      open: 'Running',
      collecting: 'Collecting',
      canceled: 'Canceled',
    };
    return (
      <div className="flex flex-col gap-1">
        <StatusBadge
          status={normalizeRunStatus(run.status === 'canceled' ? 'canceled' : 'running')}
          label={labelMap[run.status] ?? run.status}
          size="sm"
        />
        {externalTag}
      </div>
    );
  }

  const { passed, failed, flaky, skipped, total } = run.external;
  const allPassed = failed === 0;
  const Icon = allPassed ? CheckSquare : XSquare;
  const iconClass = allPassed ? 'text-status-success' : 'text-status-failed';

  const segments: GaugeSegment[] = [];
  if (passed > 0) segments.push({ label: 'passed', value: passed, color: colors.success });
  if (flaky > 0) segments.push({ label: 'flaky', value: flaky, color: colors.skipped });
  if (failed > 0) segments.push({ label: 'failed', value: failed, color: colors.failed });
  if (skipped > 0) segments.push({ label: 'skipped', value: skipped, color: colors.skipped });

  return (
    <div className="flex flex-col gap-1 min-w-0 w-full">
      <div className="flex items-center gap-2 min-w-0 overflow-hidden">
        <Icon size={16} weight="fill" className={`${iconClass} shrink-0`} />
        <Text size="xs" className="text-muted-foreground whitespace-nowrap shrink-0">
          {`${passed} passed | ${failed} failed${flaky > 0 ? ` | ${flaky} flaky` : ''}`}
        </Text>
        <div className="min-w-[48px] max-w-[150px] flex-1 basis-0">
          <MultiSegmentBar segments={segments} total={total} size="xs" />
        </div>
        {run.resultsTruncated && (
          // Without this a run that died mid-suite renders as an ordinary sealed
          // row: the counts look like a whole suite, because every test that
          // never started simply has no row to be counted.
          <TagBadge name="Partial" color="orange" size="sm" hideDot className="shrink-0" />
        )}
      </div>
      {externalTag}
    </div>
  );
};

/**
 * Exhaustive switch on `kind` — never an `else`/ternary fallthrough. Native
 * rows carry `kind?: 'cofactor'` (optional, for back-compat), so both the
 * `'cofactor'` and `undefined` cases route to `NativeStatusCell`; a future
 * third kind added to `RunsListRow` without a case here fails typecheck via
 * the `never` check in `default`, instead of silently rendering as native.
 */
export const TestRunStatusCell: React.FC<Props> = ({ execution }) => {
  switch (execution.kind) {
    case 'external':
      return <ExternalStatusCell run={execution} />;
    case 'cofactor':
    case undefined:
      return <NativeStatusCell execution={execution} />;
    default: {
      const exhaustiveCheck: never = execution;
      return exhaustiveCheck;
    }
  }
};

const NativeStatusCell: React.FC<{ execution: WorkflowTestExecutionDto }> = ({ execution }) => {
  const {
    status,
    successfulWorkflows,
    failedWorkflows,
    inconclusiveWorkflows,
    skippedWorkflows,
    quarantinedWorkflows: _quarantinedWorkflows,
    totalWorkflows,
  } = execution;
  const { config } = useTheme();
  const themeKey = config.theme;

  const tagsQuery = useTagsQuery();
  const allTags = (tagsQuery.data as { data: Tag[] } | undefined)?.data;
  // `tagFilter` is comma-joined (e.g. "smoke,regression") — a run tagged with
  // more than one tag needs one badge per tag, not a single exact-name lookup
  // that matches nothing and silently renders no badge at all.
  const runTagNames = parseTagFilter(execution.tagFilter);
  const matchedTags = runTagNames
    .map((name) => allTags?.find((t) => t.name === name))
    .filter((t): t is Tag => Boolean(t));
  const tagBadges =
    matchedTags.length > 0 ? (
      <div className="flex flex-wrap gap-1">
        {matchedTags.map((tag) => (
          <TagBadge key={tag.id} name={tag.name} color={tag.color} size="sm" />
        ))}
      </div>
    ) : null;

  if (
    status !== 'completed' &&
    status !== 'completed_with_errors' &&
    status !== 'completed_with_inconclusive'
  ) {
    const labelMap: Record<string, string> = {
      queued: 'Queued',
      running: 'Running',
      canceled: 'Canceled',
      timeout: 'Timed Out',
    };
    return (
      <div className="flex flex-col gap-1">
        <StatusBadge
          status={normalizeRunStatus(status)}
          label={labelMap[status] ?? status}
          size="sm"
        />
        {tagBadges}
      </div>
    );
  }

  const allPassed = failedWorkflows === 0;
  const colors = statusHexColors[themeKey];

  const segments: GaugeSegment[] = [];
  if (successfulWorkflows > 0) {
    segments.push({ label: 'passed', value: successfulWorkflows, color: colors.success });
  }
  if (failedWorkflows > 0) {
    segments.push({ label: 'failed', value: failedWorkflows, color: colors.failed });
  }
  if (inconclusiveWorkflows > 0) {
    segments.push({
      label: 'platform errors',
      value: inconclusiveWorkflows,
      color: colors.skipped,
    });
  }
  if (skippedWorkflows > 0) {
    segments.push({ label: 'skipped', value: skippedWorkflows, color: colors.skipped });
  }

  const isInconclusive = status === 'completed_with_inconclusive';
  const Icon = isInconclusive ? MinusSquare : allPassed ? CheckSquare : XSquare;
  const iconClass = isInconclusive
    ? 'text-status-skipped'
    : allPassed
      ? 'text-status-success'
      : 'text-status-failed';

  return (
    <div className="flex flex-col gap-1 min-w-0 w-full">
      <div className="flex items-center gap-2 min-w-0 overflow-hidden">
        <Icon size={16} weight="fill" className={`${iconClass} shrink-0`} />
        <Text size="xs" className="text-muted-foreground whitespace-nowrap shrink-0">
          {isInconclusive
            ? `${inconclusiveWorkflows} platform ${inconclusiveWorkflows === 1 ? 'error' : 'errors'}`
            : `${successfulWorkflows} passed | ${failedWorkflows} failed`}
        </Text>
        <div className="min-w-[48px] max-w-[150px] flex-1 basis-0">
          <MultiSegmentBar segments={segments} total={totalWorkflows} size="xs" />
        </div>
      </div>
      {tagBadges}
    </div>
  );
};
